export const fromLongToShort = {
  ar_AE: 'ar',
  de_DE: 'de',
  en_US: 'en',
  es_ES: 'es',
  fr_FR: 'fr',
  hi_IN: 'hi',
  hu_HU: 'hu',
  id_ID: 'id',
  it_IT: 'it',
  ja_JP: 'ja',
  ko_KO: 'ko',
  pl_PL: 'pl',
  pt_PT: 'pt',
  ro_RO: 'ro',
  ru_RU: 'ru',
  tr_TR: 'tr',
  vi_VN: 'vi',
  zh_ZH: 'zh',
  uz_UZ: 'uz',
  ky_KY: 'ky',
  fil_FIL: 'fil',
  uk_UA: 'uk',
  he_IL: 'he',
  es_MX: 'esmx',
  fa_IR: 'fa',
  th_TH: 'th',
  af_ZA: 'af',
  ur_PK: 'ur',
  bn_BD: 'bn',
  ta_IN: 'ta',
  mr_IN: 'mr',
  gu_IN: 'gu',
  te_IN: 'te',
  bg_BG: 'bg',
  sk_SK: 'sk',
  cs_CZ: 'cs',
  sr_RS: 'sr',
  hr_HR: 'hr',
  zu_ZA: 'zu',
  sl_SI: 'sl',
  mk_MK: 'mk',
  sq_AL: 'sq',
  xh_ZA: 'xh',
  pt_BR: 'ptbr'
};

export const fromShortToLong = {
  ar: 'ar_AE',
  de: 'de_DE',
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR',
  hi: 'hi_IN',
  hu: 'hu_HU',
  id: 'id_ID',
  it: 'it_IT',
  ja: 'ja_JP',
  ko: 'ko_KO',
  pl: 'pl_PL',
  pt: 'pt_PT',
  ro: 'ro_RO',
  ru: 'ru_RU',
  tr: 'tr_TR',
  vi: 'vi_VN',
  zh: 'zh_ZH',
  ky: 'ky_KY',
  uz: 'uz_UZ',
  fil: 'fil_FIL',
  uk: 'uk_UA',
  he: 'he_IL',
  esmx: 'es_MX',
  fa: 'fa_IR',
  th: 'th_TH',
  af: 'af_ZA',
  ur: 'ur_PK',
  bn: 'bn_BD',
  ta: 'ta_IN',
  mr: 'mr_IN',
  gu: 'gu_IN',
  te: 'te_IN',
  bg: 'bg_BG',
  sk: 'sk_SK',
  cs: 'cs_CZ',
  sr: 'sr_RS',
  hr: 'hr_HR',
  zu: 'zu_ZA',
  sl: 'sl_SI',
  mk: 'mk_MK',
  sq: 'sq_AL',
  xh: 'xh_ZA',
  ptbr: 'pt_BR'
};

export const sourceLanguagesForAll = {
  ar: 'ar_AE',
  de: 'de_DE',
  es: 'es_ES',
  en: 'en_US',
  fr: 'fr_FR',
  hi: 'hi_IN',
  hu: 'hu_HU',
  id: 'id_ID',
  it: 'it_IT',
  ja: 'ja_JP',
  ko: 'ko_KO',
  pt: 'pt_PT',
  pl: 'pl_PL',
  ro: 'ro_RO',
  ru: 'ru_RU',
  tr: 'tr_TR',
  vi: 'vi_VN',
  zh: 'zh_ZH',
  fil: 'fil_FIL',
  ky: 'ky_KY',
  uz: 'uz_UZ',
  uk: 'uk_UA',
  he: 'he_IL',
  esmx: 'es_MX',
  fa: 'fa_IR',
  th: 'th_TH',
  af: 'af_ZA',
  ur: 'ur_PK',
  bn: 'bn_BD',
  ta: 'ta_IN',
  mr: 'mr_IN',
  gu: 'gu_IN',
  te: 'te_IN',
  bg: 'bg_BG',
  sk: 'sk_SK',
  cs: 'cs_CZ',
  sr: 'sr_RS',
  hr: 'hr_HR',
  zu: 'zu_ZA',
  sl: 'sl_SI',
  mk: 'mk_MK',
  sq: 'sq_AL',
  xh: 'xh_ZA',
  ptbr: 'pt_BR'
};

// Every target language now shares the same unified set of source (interface)
// languages (see sourceLanguagesForAll). Kept as a function because the AI-lesson
// call sites pass a target code and expect the allowed source list back.
export const sourceLanguagesForTarget = () =>
  Object.keys(sourceLanguagesForAll);

// A class's `learnedLanguage` / a lesson's `tgtLang` are short codes; the ESP specialised-
// English variants (esp01bus, esp02med, …) are English-target and must compare equal to `en`.
// Mirrors the `.includes('esp')` collapse in `sourceLanguagesForTarget` above.
export const normalizeTargetLang = code => {
  if (!code) return '';
  const s = String(code);
  if (s.includes('esp')) return 'en';
  return fromLongToShort[s] || s; // tolerate a stray long code (en_US → en)
};

// Do two target-language codes refer to the same learnable language? Used to gate assigning
// an AI lesson to a class whose learnedLanguage differs from the lesson's target.
export const isSameTargetLanguage = (a, b) => {
  const na = normalizeTargetLang(a);
  const nb = normalizeTargetLang(b);
  return !!na && !!nb && na === nb;
};

// The locale every language name is guaranteed to exist in. dkt_HU ships only 3 of
// the 47 `languageChooser` names, and src/lang/lang.js sets no fallbackLocale, so a
// perfectly supported language (fr, es) would print its own key to a DKT teacher.
const BASE_LOCALE = 'en_US';

// One namespace lookup: the UI locale first, then the base locale, then nothing.
// Never returns the key itself - with no fallbackLocale configured, an unguarded
// $t() on a missing key renders the key at the teacher.
const langLabelIn = (vm, key) => {
  // A host without $te (a test harness mocking only $t) gives us no existence info,
  // so keep the pre-guard behaviour there rather than blanking every label. Mirrors
  // the `vm.$te && vm.$te(key)` guard in modules/aiLesson/catalogue.js.
  if (!vm.$te) return vm.$t(key);
  if (vm.$te(key)) return vm.$t(key);
  if (vm.$te(key, BASE_LOCALE)) return vm.$t(key, BASE_LOCALE);
  return '';
};

// A language name shown in the UI language when we have it, else the endonym.
// The `learnedLanguageChooser` namespace is UI-localized but only covers the core
// learnable languages (en/de/es/fr/hu + ESP); for a detected long-tail SOURCE
// language (it/ja/ru/…) it has no entry, so we fall back to the `languageChooser`
// endonym namespace (full coverage) rather than rendering the raw key. `vm` is the
// calling component (needs `$te`/`$t`).
// Pass context='source' to skip the learnedLanguageChooser lookup — that namespace
// carries target-language display names (e.g. "Angol (UK és US, felnőtt és Kids)")
// which are misleading when labelling a source language.
//
// Returns '' when the code cannot be named at ALL. The detected source language is a
// free ISO 639-1 code - the backend's language_detection_v1 schema constrains it to
// nothing - so a long-tail language like `hy` (Armenian) is missing from both
// namespaces. Prose callers must handle '' with their own wording; callers that
// cannot render blank use langLabelOrCode.
export const localizedLangLabel = (vm, code, context) => {
  if (!code) return '';
  if (context !== 'source') {
    const localized = langLabelIn(vm, `app.learnedLanguageChooser.langs.${code}`);
    if (localized) return localized;
  }
  return langLabelIn(vm, `app.languageChooser.langs.${code}`);
};

// Same lookup for places where a blank label is worse than an opaque one: a dropdown
// option or a class chip must stay readable even for a code we cannot name.
export const langLabelOrCode = (vm, code, context) =>
  localizedLangLabel(vm, code, context) || String(code || '').toUpperCase();

// Regional variants that belong to the same language. A lesson must not be
// started from any source language that shares the target's group (e.g. a
// Spanish lesson can't start from es or esmx).
export const languageVariantGroups = [
  ['es', 'esmx'],
  ['pt', 'ptbr']
];

// Target languages that may be started from their own source language.
// English lessons are allowed to run with an English source.
export const targetLanguagesAllowingSameSource = ['en'];

// Returns the source language codes that must be blocked for the given target
// language. English allows a same-language source; every other target blocks
// itself (and its regional variants).
export const getBlockedSourceLanguages = targetLanguage => {
  if (!targetLanguage) {
    return [];
  }
  if (targetLanguagesAllowingSameSource.includes(targetLanguage)) {
    return [];
  }
  const variantGroup = languageVariantGroups.find(group =>
    group.includes(targetLanguage)
  );
  return variantGroup || [targetLanguage];
};

const detectSubDomain = () => {
  try {
    const full = window.location.host;
    // window.location.host is subdomain.domain.com
    const parts = full.split('.');
    const sub = parts[0];
    return sub;
  } catch (e) {
    return undefined;
  }
};

// Detect subdomain and after browser language
export const detectBrowserLanguage = () => {
  const subDomain = detectSubDomain();

  if (subDomain && subDomain.length === 2) {
    return fromShortToLong[subDomain];
  }
  let locale = navigator.language;
  if (locale) {
    locale = locale.split('-')[0];
    locale = fromShortToLong[locale];
  }
  if (!locale) {
    locale = 'en_US';
  }
  return locale;
};



// WEBPACK FOOTER //
// ./src/components/shared/languageCodes.js